import React, { Children } from 'react';
import { CheckIcon, BookOpenIcon, LayoutDashboardIcon, MessageCircleHeartIcon, LifeBuoyIcon, UsersIcon, XIcon, CalculatorIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function FamiliesPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1047">
      <TopBar data-id="element-1048" />
      <Header data-id="element-1049" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-1050">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1051">
          <div className="flex flex-col lg:flex-row items-center gap-12" data-id="element-1052">
            {/* Left: Copy */}
            <div className="flex-1 text-left" data-id="element-1053">
              <div className="inline-block bg-[#631bb7] bg-opacity-10 text-[#631bb7] px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-1054">
                For Families
              </div>
              <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-1055">
                Your Parents Just Clicked
                <br data-id="element-1056" />
                Another Sketchy Link
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-10 max-w-2xl" data-id="element-1057">
                Aura can't teach your mom why that email was fake. Life360 can't
                help your dad understand what two-factor authentication actually
                does.{' '}
                <strong className="text-[#111111]" data-id="element-1058">
                  Caveo is the patient tech expert your family never had
                </strong>{' '}
                — no surveillance required.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4 mb-4" data-id="element-1059">
                <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-1060">
                  See How It Works
                </Link>
              </div>
              <p className="text-gray-500" data-id="element-1061">
                One subscription. Whole family. Zero surveillance.
              </p>
            </div>

            {/* Right: Photo */}
            <div className="flex-1 w-full lg:max-w-[500px]" data-id="element-1062">
              <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-1063">
                <img src="https://cdn.magicpatterns.com/uploads/ni76AqBzVEWfwitJfYbfKB/family-photo.png" alt="A grandmother and granddaughter hugging and laughing together" className="w-full h-full object-cover" data-id="element-1064" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Agitation Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-1065">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1066">
          <div className="max-w-3xl mx-auto" data-id="element-1067">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-8 text-center" data-id="element-1068">
              You Shouldn't Be Your Family's Unpaid IT Department
            </h2>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-1069">
              <p className="text-xl text-[#111111] font-medium" data-id="element-1070">
                Every Sunday, the call comes.
              </p>

              <div className="bg-white rounded-xl p-6 border-2 border-gray-200 space-y-3 italic text-gray-500" data-id="element-1071">
                <p data-id="element-1072">"The computer says my password is wrong."</p>
                <p data-id="element-1073">"Netflix isn't working."</p>
                <p data-id="element-1074">"I got an email from Amazon but I didn't order anything."</p>
                <p data-id="element-1075">"What's this charge on my credit card?"</p>
              </div>

              <p className="font-semibold text-[#111111]" data-id="element-1076">
                You've tried everything.
              </p>

              <p data-id="element-1077">
                You bought your parents McAfee (<strong data-id="element-1078">$109/year</strong>)
                because that's what Best Buy recommended. You set up Life360 (
                <strong data-id="element-1079">$300/year</strong>) so you know Mom got home safe. You
                even paid for Aura's $32/month family plan (
                <strong data-id="element-1080">$384/year</strong>) with the VPN they'll never turn on
                and the password manager they'll never use.
              </p>

              <div className="bg-red-50 rounded-xl p-6 border-2 border-red-100" data-id="element-1081">
                <p className="text-[#111111] font-bold text-xl mb-2" data-id="element-1082">
                  Three subscriptions. $793/year.
                </p>
                <p className="text-gray-600 mb-0" data-id="element-1083">
                  And your dad still asks why he needs "that extra password
                  thing" every single time.
                </p>
              </div>

              <p data-id="element-1084">
                <strong className="text-[#111111]" data-id="element-1085">
                  Here's what nobody tells you:
                </strong>{' '}
                Monitoring apps don't teach digital literacy. VPNs don't explain
                phishing. Antivirus software doesn't build confidence.
              </p>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#631bb7] border-opacity-30 my-8" data-id="element-1086">
                <p className="text-xl text-[#111111] font-semibold text-center mb-0" data-id="element-1087">
                  Your family doesn't need another app watching them.
                  <br data-id="element-1088" />
                  <span className="text-[#631bb7]" data-id="element-1089">
                    They need someone who can finally explain this stuff in a
                    way that makes sense.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution/Benefit Section */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1090">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1091">
          <div className="max-w-4xl mx-auto" data-id="element-1092">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4 text-center" data-id="element-1093">
              Meet Your Family's Digital Fiduciary
            </h2>
            <p className="text-xl text-gray-600 text-center mb-6" data-id="element-1094">
              Caveo is different because we're not selling you surveillance
              disguised as security.
            </p>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto" data-id="element-1095">
              We're the world's first{' '}
              <strong className="text-[#111111]" data-id="element-1096">Digital Fiduciary</strong> —
              which means we have a legal duty to put your family's interests
              first, not push products that pay us commissions.
            </p>

            <h3 className="text-2xl font-bold text-[#111111] mb-8 text-center" data-id="element-1097">
              What that means for your family:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1098">
              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1099">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1100">
                  <BookOpenIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1101" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1102">
                  Education that actually sticks
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1103">
                  Not 50-page PDFs nobody reads. Not YouTube tutorials your
                  parents won't watch. Just 28 plain-English lessons that teach
                  one critical skill at a time, sequenced by what threatens your
                  family most.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1104">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1105">
                  <MessageCircleHeartIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1106" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1107">
                  Honest recommendations
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1108">
                  We'll tell you that most people don't need a standalone VPN.
                  We'll explain why password reuse is more dangerous than any
                  virus. We'll help you cancel the three security subscriptions
                  you're overpaying for —{' '}
                  <strong data-id="element-1109">saving your family $500-1,000/year.</strong>
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1110">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1111">
                  <LayoutDashboardIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1112" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1113">
                  One family hub
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1114">
                  No more being "family IT support" across four time zones. Your
                  parents get the answers. You get visibility. Everyone
                  maintains dignity. Nobody loses their Friday nights.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1115">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1116">
                  <UsersIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1117" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1118">
                  Role-based access
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1119">
                  Parents learn at their own pace. Adult children can see
                  progress without hovering. Everyone gets appropriate guidance
                  for their tech comfort level.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-1120">
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1121">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1122">
                  For Adult Children
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1123">
                  <strong data-id="element-1124">Freedom.</strong> Stop answering the same tech
                  questions every Sunday. Stop worrying about the calls you'll
                  get when something goes wrong.
                </p>
              </div>
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1125">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1126">
                  For Parents
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1127">
                  <strong data-id="element-1128">Dignity.</strong> Learn without feeling judged. Get
                  clear answers without bothering your kids about "simple"
                  questions.
                </p>
              </div>
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1129">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1130">
                  For Everyone
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1131">
                  <strong data-id="element-1132">Save $500-1,000/year.</strong> Consolidate
                  subscriptions. Build lasting capability, not temporary fixes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof/Trust Section */}
      <section className="w-full bg-[#1a0647] py-16 md:py-24" data-id="element-1133">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1134">
          <div className="max-w-4xl mx-auto" data-id="element-1135">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center" data-id="element-1136">
              Built by Someone Living This Same Story
            </h2>

            <div className="bg-white bg-opacity-10 rounded-2xl p-8 mb-12" data-id="element-1137">
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1138">
                Jason Phillips spent 15 years at companies like Asana, Drift,
                and Quantcast watching the digital literacy gap widen between
                generations.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1139">
                Then his father lost money to cyber fraud. Then his cousin was
                impersonated on Facebook.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1140">
                He looked at Aura ($384/year), Life360 ($300/year), and McAfee+
                ($109/year) and realized they were all solving the wrong
                problem.
              </p>
              <p className="text-xl text-[#00f4f8] font-semibold mb-6" data-id="element-1141">
                The market was selling monitoring. Families needed education.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed" data-id="element-1142">
                So he built Caveo — the first digital security platform that
                puts teaching before tracking, designed specifically for the
                sandwich generation managing tech for aging parents.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-white text-opacity-80 mb-6 text-center" data-id="element-1143">
              From Families Like Yours:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1144">
              <div className="bg-white rounded-2xl p-6" data-id="element-1145">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1146">
                  "I bought this for my parents. Within two weeks, Mom stopped
                  calling me about every password reset. That alone was worth
                  way more than $6."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1147">— Sarah M., 42</p>
                <p className="text-sm text-gray-500" data-id="element-1148">
                  Managing care for aging parents
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6" data-id="element-1149">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1150">
                  "Caveo told me I could cancel two subscriptions I didn't need.
                  When does a security company ever do that?"
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1151">— David R.</p>
                <p className="text-sm text-gray-500" data-id="element-1152">Gen X executive</p>
              </div>
              <div className="bg-white rounded-2xl p-6" data-id="element-1153">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1154">
                  "My dad is 71 and actually understands two-factor
                  authentication now. I didn't think that was possible."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1155">— Jennifer K.</p>
                <p className="text-sm text-gray-500" data-id="element-1156">Daughter</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Breakdown */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1157">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1158">
          <div className="max-w-4xl mx-auto" data-id="element-1159">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-12 text-center" data-id="element-1160">
              What Your Family Gets
            </h2>

            <div className="space-y-8" data-id="element-1161">
              <div className="flex items-start" data-id="element-1162">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1163">
                  <BookOpenIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1164" />
                </div>
                <div data-id="element-1165">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1166">
                    The Education Platform
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1167">
                    28 modules organized by risk (not alphabet soup). Everyone
                    starts with what matters most — password hygiene, two-factor
                    auth, backup basics. Delivered in plain English your parents
                    can understand without a tech degree.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1168">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1169">
                  <LayoutDashboardIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1170" />
                </div>
                <div data-id="element-1171">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1172">
                    The Family Dashboard
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1173">
                    One place to see where everyone stands. Role-based access
                    means you can help without hovering. Parents maintain
                    dignity. Adult children get peace of mind.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1174">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1175">
                  <MessageCircleHeartIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1176" />
                </div>
                <div data-id="element-1177">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1178">
                    The Honest Advisor
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1179">
                    Most people don't need a VPN. Most free antivirus works
                    fine. Most "monitoring" services are security theater. We'll
                    tell you what your family actually needs and help you cancel
                    what you don't — saving you hundreds per year.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1180">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1181">
                  <LifeBuoyIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1182" />
                </div>
                <div data-id="element-1183">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1184">
                    The Crisis Toolkit
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1185">
                    When something goes wrong (because eventually it will),
                    nobody has to Google "what to do if..." We have step-by-step
                    response plans for every common threat — from locked
                    accounts to suspicious charges.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1186">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1187">
                  <UsersIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1188" />
                </div>
                <div data-id="element-1189">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1190">
                    Multi-Generational Design
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1191">
                    Your teenagers learn different skills than your parents.
                    Your parents get different guidance than you do. Everyone
                    gets what they need at the level that makes sense.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-1192">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1193">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1194">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1195">
              Stop Paying for Surveillance.
              <br data-id="element-1196" />
              Start Building Confidence.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-1197">
              Your family doesn't need another monitoring app. They need the
              patient tech expert they've always wanted.
            </p>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 mb-8" data-id="element-1198">
              <p className="text-lg text-gray-600 mb-6" data-id="element-1199">
                For less than one coffee per month, everyone gets:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-lg mx-auto mb-6" data-id="element-1200">
                {['28 risk-prioritized security lessons', 'One centralized family dashboard', 'Role-based access (support without hovering)', 'Honest guidance (even when it costs us a sale)', 'Crisis response plans', 'No affiliate bias. No upsells. No fear tactics.'].map((item, index) => <div key={index} className="flex items-start" data-id="element-1201">
                    <CheckIcon className="w-5 h-5 text-[#631bb7] mr-2 flex-shrink-0 mt-0.5" data-id="element-1202" />
                    <span className="text-[#111111]" data-id="element-1203">{item}</span>
                  </div>)}
              </div>
              <div className="text-center mb-6" data-id="element-1204">
                <span className="text-4xl font-extrabold text-[#111111]" data-id="element-1205">
                  $5.99
                </span>
                <span className="text-gray-500 text-lg" data-id="element-1206">/month</span>
                <span className="text-gray-500 text-lg ml-2" data-id="element-1207">
                  for your entire family
                </span>
                <p className="text-gray-500 text-sm mt-1" data-id="element-1208">
                  No contract. Cancel anytime.
                </p>
              </div>
              <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md w-full sm:w-auto" data-id="element-1209">
                Start Your Family's Digital Education
              </Link>
            </div>

            {/* Math breakdown */}
            <div className="bg-[#1a0647] rounded-2xl p-6 mb-8" data-id="element-1210">
              <div className="flex items-center justify-center mb-3" data-id="element-1211">
                <CalculatorIcon className="w-5 h-5 text-[#00f4f8] mr-2" data-id="element-1212" />
                <span className="font-bold text-white" data-id="element-1213">Do the math:</span>
              </div>
              <div className="text-white text-opacity-80 space-y-1 mb-4" data-id="element-1214">
                <p data-id="element-1215">
                  Aura ($384/year) + Life360 ($300/year) + McAfee+ ($109/year) ={' '}
                  <strong className="text-white" data-id="element-1216">$793/year</strong>
                </p>
              </div>
              <p className="text-[#00f4f8] font-bold text-xl" data-id="element-1217">
                Caveo: $72/year. Save $721. Get better education.
              </p>
            </div>

            <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20 mb-6" data-id="element-1218">
              <p className="font-bold text-[#111111] mb-2" data-id="element-1219">The Caveo Promise</p>
              <p className="text-gray-600" data-id="element-1220">
                If we don't make your family feel more confident and capable in
                the first 30 days, we'll refund you immediately. No questions.
                No hassle.
              </p>
            </div>

            <p className="text-gray-500 text-sm" data-id="element-1221">
              Join 2,000+ families who stopped paying for surveillance and
              started building capability.
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-1222" />
    </div>;
}